// ============================================================================
// useMentionSuggest.ts — состояние АВТОДОПОЛНЕНИЯ «@» для поля ввода чата.
//
// Не рисует UI: отдаёт контроллер (открыт/список/выбранный элемент/обработчики)
// компоненту ChatMentionSuggest и полю ввода. Клавиатура:
//   ↓/↑ — выбор, Enter/Tab — вставка «@login », Esc — закрыть (до следующего ввода).
// Вставка НЕ ломает существующее поведение ввода: обработанные клавиши
// возвращают true, остальные уходят дальше (Enter — отправка сообщения).
//
// Живость активного упоминания проверяется по ТЕКУЩЕМУ значению поля
// (если текст заменён извне — отправка/смена чата — список закрывается).
// ============================================================================

import { useCallback, useMemo, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';
import {
  applyMention,
  mentionCandidates,
  mentionQueryAt,
  type MentionCandidate,
  type MentionPerson,
  type MentionToken,
} from '@/lib/mentions';

interface UseMentionSuggestArgs {
  /** текущее значение поля ввода (SSoT — состояние родителя) */
  value: string;
  /** люди (участники) с логинами — источник кандидатов */
  people: readonly MentionPerson[];
  /** участники диалога (пусто — фильтр по участникам ещё не получен) */
  memberIds: readonly string[];
  inputRef: RefObject<HTMLInputElement | null>;
  setValue: (value: string) => void;
}

export interface MentionSuggestController {
  open: boolean;
  items: MentionCandidate[];
  activeIndex: number;
  /** префикс активного упоминания (для заголовка списка) */
  query: string;
  onChange: (value: string, caret: number | null) => void;
  /** true — клавиша обработана автодополнением (не отправлять сообщение) */
  onKeyDown: (e: ReactKeyboardEvent<HTMLInputElement>) => boolean;
  onBlur: () => void;
  hover: (index: number) => void;
  pick: (item: MentionCandidate) => void;
}

export function useMentionSuggest({
  value, people, memberIds, inputRef, setValue,
}: UseMentionSuggestArgs): MentionSuggestController {
  const [token, setToken] = useState<MentionToken | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Живое упоминание: запись соответствует ТЕКУЩЕМУ тексту поля
  const live = useMemo(
    () => (token && value.slice(token.start, token.end) === `@${token.query}` ? token : null),
    [token, value],
  );
  const items = useMemo(
    () => mentionCandidates(people, memberIds, live?.query ?? ''),
    [people, memberIds, live],
  );
  const open = !!live && !dismissed && value.length > 0;

  const onChange = useCallback((next: string, caret: number | null) => {
    setToken(mentionQueryAt(next, caret === null ? next.length : caret));
    setDismissed(false);
    setActiveIndex(0);
  }, []);

  const pick = useCallback((item: MentionCandidate) => {
    if (!live) return;
    const res = applyMention(value, live, item.login);
    setToken(null);
    setDismissed(false);
    setValue(res.text);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(res.caret, res.caret);
      }
    });
  }, [live, value, inputRef, setValue]);

  const onBlur = useCallback(() => setDismissed(true), []);

  const hover = useCallback((index: number) => setActiveIndex(index), []);

  const onKeyDown = useCallback((e: ReactKeyboardEvent<HTMLInputElement>): boolean => {
    if (!open) return false;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (items.length ? (i + 1) % items.length : 0));
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (items.length ? (i - 1 + items.length) % items.length : 0));
      return true;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setDismissed(true);
      return true;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (items.length === 0) return false;
      e.preventDefault();
      const item = items[Math.min(activeIndex, items.length - 1)];
      if (item) pick(item);
      return true;
    }
    return false;
  }, [open, items, activeIndex, pick]);

  return {
    open,
    items,
    activeIndex,
    query: live?.query ?? '',
    onChange,
    onKeyDown,
    onBlur,
    hover,
    pick,
  };
}
